<template>
  <el-row>
    <el-col :span="24">
      <el-form-item
        :class="{ desktop: isDesktop }"
        :error="dateError"
        label="Date range"
      >
        <DateRangeSelector v-model="range" @update:modelValue="update" />
      </el-form-item>
    </el-col>
    <el-col :span="24">
      <el-form-item
        label="Deal Type"
        prop="deal_type"
        :error="errors.deal_type"
      >
        <template #label>
          <label>
            Deal Type
            <IconWithHint>
              • CPM rate - which involves sending the agreed-upon traffic volume
              to you for an agreed-upon amount of money per thousand impressions
              (CPM) with daily limits
              <br />
              • Daily fee - which involves sending all publisher traffic with
              the agreed-upon targeting to you for an agreed-upon daily fee.
            </IconWithHint>
          </label>
        </template>
        <FlatDealTypeSelector
          v-model="flatDeal.deal_type"
          @update:modelValue="update"
        />
      </el-form-item>
    </el-col>
  </el-row>
  <template v-if="flatDeal.deal_type === FlatDealType.CpmRate">
    <el-row>
      <el-col v-if="!hideRateFields" :span="24">
        <el-form-item
          label="Impressions per day"
          prop="daily_limit"
          :error="errors.daily_limit"
        >
          <FlatDealLimit
            v-model="flatDeal.daily_limit"
            @update:modelValue="update"
            @disable="clearLimit"
            @enable="setLimit"
          />
        </el-form-item>
      </el-col>
      <el-col v-if="!hideRateFields" :span="24">
        <el-form-item
          label="Fix CPM"
          prop="flat_rate"
          :error="errors.flat_rate"
        >
          <el-input
            v-model="flatDeal.flat_rate"
            type="number"
            @update:modelValue="update"
          />
        </el-form-item>
      </el-col>
    </el-row>
  </template>
  <template v-else-if="flatDeal.deal_type === FlatDealType.DailyFee">
    <el-form-item
      v-if="!hideRateFields"
      label="Daily rate"
      prop="daily_rate"
      :error="errors.daily_rate"
    >
      <el-input
        v-model="flatDeal.daily_rate"
        type="number"
        @update:modelValue="update"
      />
    </el-form-item>
  </template>
</template>

<script lang="ts">
import { computed, defineComponent, ref, watch } from 'vue'
import DateRangeSelector from '@/apps/traforama/components/DateRangeSelector.vue'
import FlatDealTypeSelector from '@/apps/traforama/components/FlatDealTypeSelector.vue'
import FlatDealLimit from './FlatDealLimit.vue'
import useResize from '@/hooks/useResize'
import { FlatDealValidationErrorsKey, FlatDealType } from '@/api/types'
import IconWithHint from '@/components/IconWithHint.vue'
import { useUserStore } from '@/store/modules/user'
import useValidation from '@/hooks/useValidation'

export default defineComponent({
  name: 'FlatDealDetails',
  components: {
    DateRangeSelector,
    IconWithHint,
    FlatDealTypeSelector,
    FlatDealLimit
  },
  props: {
    modelValue: {
      type: Object,
      required: true
    },
    hideRateFields: {
      type: Boolean,
      default: false
    }
  },
  setup(props, context) {
    const userStore = useUserStore()

    const { isStaff } = userStore
    const flatDeal = ref({ ...props.modelValue })
    const { isDesktop } = useResize()

    const { validationErrors } = useValidation(FlatDealValidationErrorsKey)
    const errors = validationErrors()

    const disabled = computed(() => {
      return !isStaff.value
    })

    const clearLimit = () => {
      flatDeal.value.daily_limit = undefined
      flatDeal.value.deal_user_limit = false
      update()
    }

    const setLimit = () => {
      flatDeal.value.daily_limit = 1
      flatDeal.value.deal_user_limit = true
      update()
    }

    const dateError = computed(() => {
      return errors.value?.deal_start || errors.value?.deal_stop
    })

    const update = () => {
      context.emit('update', {
        deal_start: flatDeal.value.deal_start,
        deal_stop: flatDeal.value.deal_stop,
        deal_type: flatDeal.value.deal_type,
        daily_limit: flatDeal.value.daily_limit,
        deal_user_limit: flatDeal.value.deal_user_limit,
        flat_rate: flatDeal.value.flat_rate,
        daily_rate: flatDeal.value.daily_rate
      })
    }

    const range = computed({
      get: () => {
        return [flatDeal.value.deal_start, flatDeal.value.deal_stop]
      },
      set: (val) => {
        if (Array.isArray(val) && val.length === 2) {
          flatDeal.value.deal_start = val[0]
          flatDeal.value.deal_stop = val[1]
        } else {
          flatDeal.value.deal_start = ''
          flatDeal.value.deal_stop = ''
        }
        update()
      }
    })

    watch(
      (_: any) => flatDeal.value.deal_type,
      (_) => {
        flatDeal.value.daily_limit = undefined
        flatDeal.value.flat_rate = undefined
        flatDeal.value.daily_rate = undefined
        update()
      }
    )

    watch(
      () => props.modelValue,
      (val: any) => {
        flatDeal.value = { ...flatDeal.value, ...val }
      },
      { deep: true }
    )

    return {
      range,
      isDesktop,
      flatDeal,
      update,
      errors,
      disabled,
      clearLimit,
      setLimit,
      FlatDealType,
      dateError
    }
  }
})
</script>

<style lang="scss" scoped>
.el-form-item__content > div {
  vertical-align: top;
}
</style>

<style lang="scss">
.desktop .el-collapse {
  .el-collapse-item__content {
    .el-form-item__content {
      margin-left: 0 !important;
    }
    .el-form-item__label {
      width: inherit !important;
    }
  }
}
.ip-range-types {
  margin-bottom: 0 !important;
  margin-top: 26px !important;
}
.ip-ranges-input {
  margin-top: 0 !important;
}
</style>
